import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { FamilyMembersService } from '../../services/family-members.service';
import { SubscriptionStatusTagComponent } from '../../shared/components/subscription-status-tag/subscription-status-tag.component';
import { FAMILY_PRIMARY_TAG_LABEL } from '../../shared/utils/family-tags.util';
import { ToastService } from '../../services/toast.service';
import {
  SavedCard,
  SubscriptionPaymentMethodService,
} from '../../services/subscription-payment-method.service';
import {
  PaymentMethodModalComponent,
  PaymentMethodSubject,
} from '../../shared/components/payment-method-modal/payment-method-modal.component';
import { renewalCardUpdatedToast } from '../../shared/utils/payment-method.copy';
import { FamilyMemberEditService } from '../../services/family-member-edit.service';
import { FamilyRemoveFlowController } from '../../shared/utils/family-remove.controller';
import { FamilyRemoveModalComponent } from '../../shared/components/family-remove-modal/family-remove-modal.component';
import { FamilyMoveModalComponent } from '../../shared/components/family-move-modal/family-move-modal.component';
import { FamilyMoveFlowController } from '../../shared/utils/family-move.controller';
import { canRemoveRow } from '../../shared/utils/family-remove.util';
import { FamilyMemberEditController } from '../../shared/utils/family-member-edit.controller';
import { FamilyMemberFormComponent } from '../../shared/components/family-member-form/family-member-form.component';
import { ManageSubscriptionMenuComponent } from '../../shared/components/manage-subscription-menu/manage-subscription-menu.component';
import { US_STATES } from '../../shared/constants/us-states.constant';
import { FamilyMembersModalComponent } from '../../shared/components/family-members-modal/family-members-modal.component';
import { FamilyMembersFlowController } from '../../shared/utils/family-members-flow.controller';
import { CancellationSettingsService } from '../../services/cancellation-settings.service';
import { PlanChangeSettingsService } from '../../services/plan-change-settings.service';
import { PlanChangeResult, buildPlanChangeNotice } from '../../services/plan-change.service';
import { PlanChangePanelComponent } from '../notification-modal/components/plan-change-panel/plan-change-panel.component';
import {
  CancelRefundDone,
  CancelRefundPanelComponent,
} from '../../shared/components/cancel-refund-panel/cancel-refund-panel.component';
import type { FamilyPlanView } from '../../shared/types/family-plan-view.types';
import {
  FamilyPlanRow,
  addVehicleSubject,
  canAddVehicle,
  canCancelFamilyRow,
  canChangeFamilyRowPlan,
  canMoveFamilyRow,
  canUpdatePaymentMethod,
  familyPaymentSubject,
  familyPlanRows,
  isAtVehicleLimit,
  isSecondaryView,
  planChangeToast,
  seatsLabel,
} from '../../shared/utils/family-plan-view.util';

/**
 * Family Plan tab (CWS-1582): one card per plate, primary first, with each plate's membership
 * details and history. The primary's view can add vehicles; a member sees the primary and their own plate.
 *
 * @selector app-family-plan-tab
 * @input view - The customer's family, loaded by the host.
 * @output changed - Something was added: the host reloads the view.
 */
@Component({
  selector: 'app-family-plan-tab',
  standalone: true,
  imports: [
    CommonModule,
    SubscriptionStatusTagComponent,
    FamilyMembersModalComponent,
    FamilyMemberFormComponent,
    ManageSubscriptionMenuComponent,
    PaymentMethodModalComponent,
    FamilyRemoveModalComponent,
    FamilyMoveModalComponent,
    CancelRefundPanelComponent,
    PlanChangePanelComponent,
  ],
  templateUrl: './family-plan-tab.component.html',
  styleUrls: ['./family-plan-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FamilyPlanTabComponent implements OnInit, OnDestroy {
  @Input() set view(value: FamilyPlanView | null) {
    this._view = value;
    this.rows = familyPlanRows(value);
    this.seats = seatsLabel(value);
    this.showAdd = canAddVehicle(value);
    this.atLimit = isAtVehicleLimit(value);
    this.secondary = isSecondaryView(value);
    this.loadCancelAvailability(value?.group?.location_id ?? null);
    this.loadPlanChangeAvailability(value?.group?.location_id ?? null);
  }
  get view(): FamilyPlanView | null {
    return this._view;
  }
  @Output() changed = new EventEmitter<void>();

  readonly primaryLabel = FAMILY_PRIMARY_TAG_LABEL;

  rows: FamilyPlanRow[] = [];
  seats = '';
  showAdd = false;
  atLimit = false;
  secondary = false;

  readonly states = US_STATES;
  /** The shop has Update Payment Method switched on (asked once, answered from a cache). */
  paymentMethodOn = false;
  paymentSubject: PaymentMethodSubject | null = null;
  /** CWS-1579: cancel & refund is on at the family's shop. */
  cancelOn = false;
  /** The plate whose membership the cancel & refund panel is open on. */
  cancelTarget: { subscriptionId: string; locationId: string; customerName: string } | null = null;
  private cancelLocationId: string | null = null;
  /** CWS-1579: plan changes are on at the family's shop. */
  planChangeOn = false;
  /** The plate the Update membership panel is open on; the panel takes the tab's place. */
  planChangeTarget: { subscriptionId: string; locationId: string } | null = null;
  private planChangeLocationId: string | null = null;
  readonly membersFlow: FamilyMembersFlowController;
  readonly editFlow: FamilyMemberEditController;
  readonly removeFlow: FamilyRemoveFlowController;
  readonly moveFlow: FamilyMoveFlowController;

  private _view: FamilyPlanView | null = null;
  private readonly openHistory = new Set<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    familyMembers: FamilyMembersService,
    editService: FamilyMemberEditService,
    private readonly paymentMethods: SubscriptionPaymentMethodService,
    private readonly toast: ToastService,
    private readonly cancellationSettings: CancellationSettingsService,
    private readonly planChangeSettings: PlanChangeSettingsService
  ) {
    this.removeFlow = new FamilyRemoveFlowController(familyMembers, {
      onRemoved: member => {
        this.toast.showSuccess(
          'Plate removed',
          `${member.vehicle.license_plate ?? 'The plate'} is no longer in the family plan.`
        );
        this.changed.emit();
      },
    });
    this.moveFlow = new FamilyMoveFlowController(familyMembers, {
      onMoved: member => {
        this.toast.showSuccess(
          'Plate moved',
          `${member.vehicle.license_plate ?? 'The plate'} now has a number of its own.`
        );
        this.changed.emit();
      },
    });
    this.editFlow = new FamilyMemberEditController(editService, {
      onSaved: () => {
        this.toast.showSuccess('Details saved', 'The family member was updated.');
        this.changed.emit();
      },
      onPartial: () => this.changed.emit(),
    });
    this.membersFlow = new FamilyMembersFlowController(familyMembers, {
      onChanged: () => {
        this.toastAdded();
        this.changed.emit();
      },
    });
  }

  ngOnInit(): void {
    this.paymentMethods
      .isEnabled()
      .pipe(takeUntil(this.destroy$))
      .subscribe(on => (this.paymentMethodOn = on));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.membersFlow.destroy();
    this.editFlow.destroy();
    this.removeFlow.destroy();
    this.moveFlow.destroy();
  }

  trackByRow(_index: number, row: FamilyPlanRow): string {
    return row.key;
  }

  isHistoryOpen(row: FamilyPlanRow): boolean {
    return this.openHistory.has(row.key);
  }

  toggleHistory(row: FamilyPlanRow): void {
    if (this.openHistory.has(row.key)) {
      this.openHistory.delete(row.key);
    } else {
      this.openHistory.add(row.key);
    }
  }

  /** One toast per plate that went in; the pop-up's done step still lists every result. */
  private toastAdded(): void {
    this.membersFlow.outcomes
      .filter(outcome => outcome.status === 'added')
      .forEach(outcome =>
        this.toast.showSuccess(
          'Family member added',
          `"${outcome.label}" has been added successfully.`
        )
      );
  }

  canRemove(row: FamilyPlanRow): boolean {
    return canRemoveRow(this._view, row.contactLocked);
  }

  canUpdatePaymentMethod(row: FamilyPlanRow): boolean {
    return canUpdatePaymentMethod(row, this.paymentMethodOn);
  }

  openPaymentMethod(row: FamilyPlanRow): void {
    this.paymentSubject = familyPaymentSubject(row.member);
  }

  closePaymentMethod(): void {
    this.paymentSubject = null;
  }

  onPaymentMethodUpdated(card: SavedCard): void {
    const toast = renewalCardUpdatedToast(card, this.paymentSubject);
    this.paymentSubject = null;
    this.toast.showSuccess(toast.title, toast.detail);
    this.changed.emit();
  }

  canCancel(row: FamilyPlanRow): boolean {
    return canCancelFamilyRow(this._view, row, this.cancelOn);
  }

  /** Opens cancel & refund on the plate; on the primary it offers Cancel all / Cancel primary only. */
  cancelMembership(row: FamilyPlanRow): void {
    const locationId = this._view?.group?.location_id;
    if (!this.canCancel(row) || !locationId) return;
    this.cancelTarget = {
      subscriptionId: row.subscriptionId,
      locationId,
      customerName: row.customerName,
    };
  }

  closeCancel(): void {
    this.cancelTarget = null;
  }

  onCancelDone(done: CancelRefundDone): void {
    this.cancelTarget = null;
    if (done.result.status === 'failed') {
      this.toast.showWarn('Membership not cancelled', done.summary);
    } else {
      this.toast.showSuccess('Membership cancelled', done.summary);
    }
    this.changed.emit();
  }

  /** Asked once per shop; a failure leaves Cancel hidden, the API re-checks anyway. */
  private loadCancelAvailability(locationId: string | null): void {
    if (locationId === this.cancelLocationId) return;
    this.cancelLocationId = locationId;
    this.cancelOn = false;
    if (!locationId) return;
    this.cancellationSettings
      .getAvailability(locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (this.cancelLocationId === locationId) {
            this.cancelOn = response?.data?.enabled === true;
          }
        },
        error: () => (this.cancelOn = false),
      });
  }

  canChangePlan(row: FamilyPlanRow): boolean {
    return canChangeFamilyRowPlan(this._view, row, this.planChangeOn);
  }

  /** Opens Update membership on the plate, at the family's shop. */
  changePlan(row: FamilyPlanRow): void {
    const locationId = this._view?.group?.location_id;
    if (!this.canChangePlan(row) || !locationId) return;
    this.planChangeTarget = { subscriptionId: row.subscriptionId, locationId };
  }

  closePlanChange(): void {
    this.planChangeTarget = null;
  }

  onPlanChangeApplied(result: PlanChangeResult): void {
    this.planChangeTarget = null;
    const toast = planChangeToast(buildPlanChangeNotice(result));
    if (toast) this.toast.showSuccess(toast.title, toast.detail);
    this.changed.emit();
  }

  /** Asked once per shop; a failure leaves Update hidden, the API re-checks anyway. */
  private loadPlanChangeAvailability(locationId: string | null): void {
    if (locationId === this.planChangeLocationId) return;
    this.planChangeLocationId = locationId;
    this.planChangeOn = false;
    if (!locationId) return;
    this.planChangeSettings
      .getAvailability(locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (this.planChangeLocationId === locationId) {
            this.planChangeOn = response?.data?.enabled === true;
          }
        },
        error: () => (this.planChangeOn = false),
      });
  }

  canMove(row: FamilyPlanRow): boolean {
    return canMoveFamilyRow(this._view, row);
  }

  /** Opens "Move to own number" on a plate that shares the primary's customer (CWS-1582 §7). */
  moveToOwnNumber(row: FamilyPlanRow): void {
    if (this.canMove(row)) this.moveFlow.open(this._view, row.member);
  }

  removeMember(row: FamilyPlanRow): void {
    this.removeFlow.open(this._view, row.member);
  }

  editDetails(row: FamilyPlanRow): void {
    this.editFlow.open(row.member, this._view?.members ?? [], row.contactLocked);
  }

  addVehicle(): void {
    const subject = addVehicleSubject(this._view);
    if (subject && this._view?.group) {
      this.membersFlow.open(this._view.group.id, subject);
    }
  }
}
